/*
 * Copyright (c) 2021, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React, { useEffect } from 'react';
import { makeStyles, Theme } from '@material-ui/core/styles';

import { Grid, Tooltip, Typography } from '@material-ui/core';
import { FormattedMessage } from 'react-intl';
import { PrimaryButtonRounded} from '../../elements/PrimaryButtonRounded';
import { HomeRounded, LockOpenRounded } from '@material-ui/icons';
import { useHistory, useParams } from 'react-router-dom';
import Settings from '../../conf/settings';
import { addExternalUserToOrg } from '../../data/operations';
import { connect } from 'react-redux';
import { AddExternalUserToOrgReducer, ApplicationState, StorageOperations } from '../../data/interfaces';
import { useSnackbar } from 'notistack';
import { PreLoaderLogo } from '../../elements/PreLoaderLogo';
import { RESOURCE_SIGN_IN, RETURN_TO_PATH, SELECTED_ORG_HANDLE_NAME } from '../../data/constants';
import { localStoreOperation, sessionStoreOperation } from '../../data/utils';

const useStyles = makeStyles((theme: Theme) => ({
    appBarSpacer: {
        minHeight: theme.spacing(16)
    },
    noPermissionTitle: {
        ...theme.custom.noPermission.styles,
        [theme.breakpoints.down('sm')]: {
            textAlign: 'center'
        }
    },
    content: {
        flexGrow: 1,
        height: '100vh',
        overflow: 'auto',
        ...theme.custom.homePage.styles
    },
    loaderContainer: {
        width: 'cal(100%-75px)',
        height: '60vh',
        backgroundColor: theme.palette.secondary.light,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    buttonSpacer: {
        minHeight: theme.spacing(8)
    },
}));

export interface NoPermissionProps {
    addExternalUserToOrgReducer: AddExternalUserToOrgReducer
    addExternalUserToOrg: (organizationHandle: string) => Promise<number|void>
}

function NoPermission({ addExternalUserToOrgReducer, addExternalUserToOrg } : NoPermissionProps) {
    const classes = useStyles();
    const { orgName } = useParams<{orgName: string}>();
    const history = useHistory();
    const { enqueueSnackbar } = useSnackbar();
    const { loading, error } = addExternalUserToOrgReducer;
    const isChoreoControlPlaneOrg: boolean = Settings.choreoControlPlaneOrgHandleName === orgName;

    const onRequestAccessClick = async () => {
        const status = await addExternalUserToOrg(orgName);
        if (status === 200) {
            sessionStoreOperation(StorageOperations.ADD, RETURN_TO_PATH, window.location.pathname);
            window.location.reload();
        }
    };

    const onGoBackHomeClick = () => {
        const organizationHandle = localStoreOperation(StorageOperations.GET, SELECTED_ORG_HANDLE_NAME);
        const redirectURL = organizationHandle ? '/' + organizationHandle : RESOURCE_SIGN_IN;
        history.push(redirectURL);
    };

    useEffect(() => {
        if (error) {
            enqueueSnackbar('Error while adding user to the requested organization', { variant: 'error' })
        }
    }, [addExternalUserToOrgReducer]);

    return (
        <main className={classes.content}>
            <div className={classes.appBarSpacer} />
            {
                loading ?
                    <div className={classes.loaderContainer}>
                        <PreLoaderLogo size='medium' />
                    </div>
                    :
                    <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
                        <img src='/themes/default/images/page-not-found.svg'
                        />
                        <Typography component='div' className={classes.noPermissionTitle}>
                            <FormattedMessage
                                id='noPermission.message'
                                defaultMessage='You do not have permission to view this page, or the page does not exist.'
                            />
                        </Typography>
                        {isChoreoControlPlaneOrg ?
                            <div>
                                <Tooltip title={'Request Access'}>
                                    <PrimaryButtonRounded
                                        onClick={onRequestAccessClick}
                                        startIcon={<LockOpenRounded/>}
                                        text='Request Access'
                                    />
                                </Tooltip>
                                <div className={classes.buttonSpacer} />
                                <Tooltip title={'Go Back Home'}>
                                    <PrimaryButtonRounded
                                        onClick={onGoBackHomeClick}
                                        startIcon={<HomeRounded/>}
                                        text='Go Back Home'
                                    />
                                </Tooltip>
                            </div>
                            :
                            <Tooltip title={'Go Back Home'}>
                                <PrimaryButtonRounded
                                    onClick={onGoBackHomeClick}
                                    startIcon={<HomeRounded/>}
                                    text='Go Back Home'
                                />
                            </Tooltip>
                        }
                    </Grid>
            }
        </main>
    );
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        addExternalUserToOrgReducer: state.addExternalUserToOrgReducer,
    }
}

const mapDispatchToProps = {
    addExternalUserToOrg: addExternalUserToOrg
}

export default connect(mapStateToProps, mapDispatchToProps)(NoPermission);
